import { Injectable } from "@angular/core";
import { Object3DUserData } from "@utils/shape";
import { Mesh } from "three";
import { Sides } from "../models/helper.model";
import { Face, FaceType } from "~/src/utils/count-calculator/count-calculator.model";
import { FaceMetaDataMap } from "~/src/utils/shape-facetype";

@Injectable({
    providedIn: "root",
})
export class SetShapeUserDataService {
    /*
    This method performs the detection and updation of the face data each time the new shape is generated.
  */
    public adjacentData(objects: Mesh[], shape: Mesh): void {
        const adjacentObjects: { side: string; uuid: string }[] = [];

        /*
      Criteria here refers to the conditions to get all the shapes if present in the nearby grids.
      We only fetch the data of nearest 6 grids to optomise.
    */
        const criteria = {
            right: shape.userData[Object3DUserData.cellReference].row - 1,
            left: shape.userData[Object3DUserData.cellReference].row + 1,
            back: shape.userData[Object3DUserData.cellReference].column - 1,
            front: shape.userData[Object3DUserData.cellReference].column + 1,
            top: shape.userData[Object3DUserData.cellReference].cell + 1,
            bottom: shape.userData[Object3DUserData.cellReference].cell - 1,
        };

        for (const side in Sides) {
            const checkCriteria: any = {
                row: shape.userData[Object3DUserData.cellReference].row,
                column: shape.userData[Object3DUserData.cellReference].column,
                cell: shape.userData[Object3DUserData.cellReference].cell,
            };

            switch (side) {
                case Sides.Right:
                    checkCriteria.row = criteria.right;
                    break;
                case Sides.Left:
                    checkCriteria.row = criteria.left;
                    break;
                case Sides.Back:
                    checkCriteria.column = criteria.back;
                    break;
                case Sides.Front:
                    checkCriteria.column = criteria.front;
                    break;
                case Sides.Top:
                    checkCriteria.cell = criteria.top;
                    break;
                case Sides.Bottom:
                    checkCriteria.cell = criteria.bottom;
                    break;
            }

            for (const object of objects) {
                if (
                    object !== shape &&
                    object.userData[Object3DUserData.cellReference] &&
                    Object.keys(checkCriteria).every(
                        (key) => object.userData[Object3DUserData.cellReference][key] === checkCriteria[key],
                    )
                ) {
                    adjacentObjects.push({
                        side: side,
                        uuid: object.uuid,
                    });
                    shape.userData[Object3DUserData.adjacentData] = shape.userData[Object3DUserData.adjacentData] || {};
                    object.userData[Object3DUserData.adjacentData] =
                        object.userData[Object3DUserData.adjacentData] || {};

                    // We update the userdata here for both the shapes.
                    shape.userData[Object3DUserData.adjacentData][side] = object.uuid;
                    object.userData[Object3DUserData.adjacentData][this.oppositeSide(side)] = shape.uuid;
                }
            }
        }
    }

    // This will get the opposite side for the stationary shapes.
    private oppositeSide(side: string): string {
        switch (side) {
            case Sides.Right:
                return Sides.Left;
            case Sides.Left:
                return Sides.Right;
            case Sides.Front:
                return Sides.Back;
            case Sides.Back:
                return Sides.Front;
            case Sides.Top:
                return Sides.Bottom;
            case Sides.Bottom:
                return Sides.Top;
            default:
                return "";
        }
    }

    public clearAdjacentDataOnDragStart(mesh: Mesh, allmeshes: Mesh[]): void {
        const adjacentMeshes = this.getAdjacentMeshes(mesh, allmeshes);
        for (const adjacentMesh of adjacentMeshes) {
            if (adjacentMesh.userData[Object3DUserData.adjacentData]) {
                this.removeAdjacentReferencesToMesh(adjacentMesh, mesh.uuid);
            }
        }

        delete mesh.userData[Object3DUserData.adjacentData];
        delete mesh.userData[Object3DUserData.connectionData];
        delete mesh.userData[Object3DUserData.facePattern];
    }

    private removeAdjacentReferencesToMesh(adjacentMesh: Mesh, deletingMeshUUID: string): void {
        const adjacentData = adjacentMesh.userData[Object3DUserData.adjacentData];
        const connectionData: { face: Face; connectionType: FaceType }[] =
            adjacentMesh.userData[Object3DUserData.connectionData];

        if (!adjacentData) return;

        for (let faceKey in adjacentData) {
            const face = faceKey as Face;
            if (adjacentData[face] === deletingMeshUUID) {
                const meshRealFace = this.getMeshRealFace(adjacentMesh, face);
                if (connectionData?.length) {
                    const connection = connectionData.find((data) => data.face === meshRealFace);
                    const adjacentFaceMeta = (
                        adjacentMesh.userData[Object3DUserData.faceMetaDataMap] as FaceMetaDataMap
                    )[meshRealFace];

                    if (connection && adjacentFaceMeta) {
                        connection.connectionType = adjacentFaceMeta.originalType;
                    }
                }
                delete adjacentData[face];
            }
        }

        if (connectionData?.length) {
            const facePatternData = connectionData
                .map((data) => (data.connectionType === FaceType.HalfPlane ? "HP" : data.connectionType[0]))
                .join(" - ");

            adjacentMesh.userData[Object3DUserData.facePattern] = facePatternData;
        }
        if (Object.keys(adjacentData).length === 0) {
            delete adjacentMesh.userData[Object3DUserData.adjacentData];
        }
    }

    public getMeshRealFace(mesh: Mesh, currentFaceDirection: Face): Face {
        switch (currentFaceDirection) {
            case Face.Front:
                return mesh.userData[Object3DUserData.orientationData][0];
            case Face.Right:
                return mesh.userData[Object3DUserData.orientationData][1];
            case Face.Back:
                return mesh.userData[Object3DUserData.orientationData][2];
            case Face.Left:
                return mesh.userData[Object3DUserData.orientationData][3];
            case Face.Top:
                return mesh.userData[Object3DUserData.orientationData][4];
            case Face.Bottom:
                return mesh.userData[Object3DUserData.orientationData][5];
        }
    }

    private getAdjacentMeshes(mesh: Mesh, allMeshes: Mesh[]): Mesh[] {
        if (!mesh.userData[Object3DUserData.adjacentData]) {
            return [];
        }
        const shapeAdjacentData = mesh.userData[Object3DUserData.adjacentData];
        const adjacentUUIDs = Object.values(shapeAdjacentData);

        return allMeshes.filter((mesh) => adjacentUUIDs.includes(mesh.uuid));
    }
}
